import React, { useState, useRef } from "react";
import { Input, Button, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Highlighter from "react-highlight-words";

// 用於處理表格中的搜索功能，它返回了一個包含多個函數和變數的物件。
export const useSearchColumn = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const { t } = useTranslation();
  const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText("");
  };

  const searchInput = useRef("");

  const getColumnSearchProps = (dataIndex: any) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: {
      setSelectedKeys: any;
      selectedKeys: any;
      confirm: any;
      clearFilters: any;
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          maxLength={30}
          ref={(node: any) => {
            searchInput.current = node;
          }}
          // placeholder={{t("ISMS.Search ")}}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t("ISMS.Search")}
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            {t("ISMS.Reset")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value: any, record: any) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : "",
    onFilterDropdownVisibleChange: (visible: any) => {
      if (visible) {
        //@ts-ignore
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  return getColumnSearchProps;
};
/* 根據 fileName 判斷所屬的模型，然後返回相應的模型名稱。
   函數內部使用一系列 includes 判斷，根據 fileName 的部分字串進行模型匹配。*/
export const FileToModel = (fileName: string) => {
  switch (true) {
    case fileName.includes("012C"):
      return "M300";
    case fileName.includes("0136"):
      return "M301-G";
    case fileName.includes("012D"):
      return "M301";
    case fileName.includes("016C"):
      return "M301-GW";
    case fileName.includes("014A"):
      return "M330";
    case fileName.includes("014B"):
      return "M330-W";
    case fileName.includes("014C"):
      return "M330-W5";
    case fileName.includes("015E"):
      return "M350";
    case fileName.includes("015F"):
      return "M350-4/6/12/5G";
    case fileName.includes("0160"):
      return "M350-44/66";

    case fileName.includes("01F4"):
      return "M360-P";

    case fileName.includes("016E"):
      return "M366-W4";
    default:
      return "Others";
  }
};

// function getResponse(type: string) {
//   if (type.includes("dhcp")) {
//     return "DHCP";
//   } else if (type.includes("static")) {
//     return "Static_IP";
//   } else if (type.includes("pppoe")) {
//     return "PPPOE";
//   }
// }

// M300: 012C #Not available in the future
// M301: 012D #Not available in the future
// M301-G: "0136" #-----Ori, New rename (310)
// M301-GW: 016C #-----Ori, New (364)

// M330-W: 014B #-----Ori, New (331)
// M330-W5: 014C #-----Ori, New (332)
// M330: 014A #Not available in the future

// M350-4/6/12/5G: 015F #Not available in the future
// M350-44/66: "0160" #Not available in the future
// M350: 015E #-----Ori, New rename (350)

// M360-P: 01F4 #-----Ori, New (500)
// M366-W4: 016E #-----New (366)

/* 處理提供的身份數據，該身份數據包含了許多關於設備的信息
   使用了Set 變數，來確保生成的選項是唯一的。
   提取和生成設備屬性的唯一選項，能夠更方便地在應用程序中進行設備的查詢和選擇。
*/

export const getWanEthApn = (item: any): string | null => {
  const wanPriority = item?.obj?.wan?.priority;
  const wanConnectTable = item?.obj?.wan?.connect_table;
  const activeIndex = wanConnectTable?.active_profile;
  const activeProfile = activeIndex && wanConnectTable.profile[activeIndex - 1]?.profile_list;
  // 格式 1: 有 profile_list
  if (activeProfile) {
    for (let i = 0; i < activeProfile.length; i++) {
      if (activeProfile[i]?.interface?.includes("wan")) {
        return activeProfile[i]?.protocol;
      }
    }
  }
  // 格式 2: 有 priority.includes("eth") && ethernet.type
  if (wanPriority?.order?.includes("eth")) {
    return item?.obj?.wan?.ethernet?.type ?? null;
  }

  // 格式 3: 無 connect_table, 無 priority，但有 ethernet.type
  if (activeProfile === undefined && wanPriority === undefined) {
    return item?.obj?.wan?.ethernet?.type ?? null;
  }
  return null;
};

const getValidApn = (apnList: any[]): string | null => {
  if (Array.isArray(apnList)) {
    const first = apnList[0]?.apn?.trim();
    if (first) return first;

    const second = apnList[1]?.apn?.trim();
    if (second) return second;
  }
  return null;
};

export const getSim1Apn = (item: any) => {
  if (Array.isArray(item?.obj?.lte?.sim)) {
    return getValidApn(item?.obj?.lte?.sim[0]?.apn) ?? null;
  }
  return null; // Default case if no conditions match
};

export const getSim2Apn = (item: any) => {
  if (Array.isArray(item?.obj?.lte?.sim)) {
    return getValidApn(item?.obj?.lte?.sim[1]?.apn) ?? null;
  }
  return null; // Default case if no conditions match
};

const getWanProtocols = (item: any): Set<string> => {
  const result = new Set<string>();

  const wan = item?.obj?.wan;
  const ethType = wan?.ethernet?.type;
  const wanPriority = wan?.priority;
  const connectTable = wan?.connect_table;
  const activeIndex = connectTable?.active_profile;
  const activeProfile = connectTable?.profile?.[activeIndex - 1]?.profile_list;

  // 格式 1: 有 profile_list
  if (Array.isArray(activeProfile)) {
    for (const profile of activeProfile) {
      if (profile?.interface?.includes("wan") && profile?.protocol) {
        result.add(profile.protocol);
      }
    }
  }

  // 格式 2: 有 priority.includes("eth") && ethernet.type
  if (wanPriority?.order?.includes("eth") && ethType != null) {
    result.add(ethType);
  }

  // 格式 3: 無 connect_table, 無 priority，但有 ethernet.type
  if (!activeProfile && !wanPriority && ethType != null) {
    result.add(ethType);
  }

  return result;
};

export const getOptionsFromIdentity = (identity: any) => {
  let models: any = new Set();
  let aMCSV: any = new Set();
  let bMCSV: any = new Set();
  let locations: any = new Set();
  let groups: any = new Set();
  let currentMCSV: any = new Set();
  let upgradeMCSV: any = new Set();
  let wanEth: any = new Set();
  let sim1Apn: any = new Set();
  let sim2Apn: any = new Set();

  // console.log("print identity", identity); // FIXME: gid.length -> undefined
  let deviceIdentity = identity?.response?.device_cfg
    ?.filter((item: any) => item.nodeInf.health !== "offline" && item.obj !== "No Response!")
    ?.map((item: any) => {
      let identityData = item?.obj?.identity?.partition;
      let activeObj = identityData?.filter((partition: any) => partition?.active);
      let simList = item?.obj?.lte?.sim;

      models.add(item.nodeInf.model);
      if (identityData?.[0]?.software_mcsv) {
        aMCSV.add(identityData[0].software_mcsv);
      }
      if (identityData?.[1]?.software_mcsv) {
        bMCSV.add(identityData[1].software_mcsv);
      }
      if (item.nodeInf.city) {
        locations.add(item.nodeInf.city);
      }
      if (item?.nodeInf?.gid?.length) {
        item.nodeInf.gid.forEach((item: string) => groups.add(item));
      }
      if (item?.obj?.identity?.active_image_partition === "a") {
        currentMCSV.add(identityData?.[0]?.software_mcsv);
      } else {
        currentMCSV.add(identityData?.[1]?.software_mcsv);
      }

      if (item?.obj?.identity?.active_image_partition === "a") {
        upgradeMCSV.add(identityData?.[1]?.software_mcsv);
      } else {
        upgradeMCSV.add(identityData?.[0]?.software_mcsv);
      }
      // 已當前狀態為 Filter
      // WAN
      const wanProtocols = getWanProtocols(item);
      wanProtocols.forEach((protocol) => wanEth.add(protocol));

      if (Array.isArray(simList)) {
        // SIM_1 APN
        const sim1 = simList[0]?.apn;
        if (getValidApn(sim1) !== null) {
          sim1Apn.add(getValidApn(sim1));
        }

        // SIM_2 APN
        const sim2 = simList[1]?.apn;
        if (getValidApn(sim2) !== null) {
          sim2Apn.add(getValidApn(sim2));
        }
      }
      return {
        id: item.nodeInf.id,
        name: item.nodeInf.name,
        model: item.nodeInf.model,
        active: activeObj?.[0]?.name,
        a: identityData?.[0]?.software_mcsv,
        b: identityData?.[1]?.software_mcsv,
        location: item.nodeInf.city,
        group: item.nodeInf.gid,
        currentMCSV:
          item?.obj?.identity?.active_image_partition === "a"
            ? identityData?.[0]?.software_mcsv
            : identityData?.[1]?.software_mcsv,
        upgradeMCSV:
          item?.obj?.identity?.active_image_partition === "a"
            ? identityData?.[1]?.software_mcsv
            : identityData?.[0]?.software_mcsv,
        wanEth: getWanEthApn(item),
        sim1Apn: getSim1Apn(item),
        sim2Apn: getSim2Apn(item),
      };
    });

  aMCSV = Array.from(aMCSV);
  bMCSV = Array.from(bMCSV);
  models = Array.from(models);
  locations = Array.from(locations);
  groups = Array.from(groups);
  currentMCSV = Array.from(currentMCSV);
  upgradeMCSV = Array.from(upgradeMCSV);
  wanEth = Array.from(wanEth);
  sim1Apn = Array.from(sim1Apn);
  sim2Apn = Array.from(sim2Apn);

  return {
    deviceIdentity,
    aMCSV,
    bMCSV,
    models,
    locations,
    groups,
    currentMCSV,
    upgradeMCSV,
    wanEth,
    sim1Apn,
    sim2Apn,
  };
};
